import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, TrendingUp, Package } from 'lucide-react';
import PageWrapper from '../PageWrapper/PageWrapper';
import { PermissionFlags } from '../../components/PermissionFlags/PermissionFlags';
import CommissionContractsStatisticsAPI from '../../api/CommissionContractsStatisticsAPI';
import TotalCard from './components/TotalCard';
import ChartCard from './components/ChartCard';
import PercentageCard from './components/PercentageCard';
import type { InsightsTimeRange } from '../../insights/insightsTimeRange';
import { getMonthToDateRange } from '../../insights/insightsTimeRange';
import { InsightsTimeRangeControls } from '../../components/InsightsTimeRangeControls/InsightsTimeRangeControls';

type TimeRange = InsightsTimeRange;

function CommissionContractsStatisticsPage() {
  const { t } = useTranslation();
  const [timeRange, setTimeRange] = useState<TimeRange>(() => getMonthToDateRange());

  // Percentage states
  const [soldPercentage, setSoldPercentage] = useState(0);
  const [soldNumerator, setSoldNumerator] = useState(0);
  const [soldDenominator, setSoldDenominator] = useState(0);
  const [returnedPercentage, setReturnedPercentage] = useState(0);
  const [returnedNumerator, setReturnedNumerator] = useState(0);
  const [returnedDenominator, setReturnedDenominator] = useState(0);
  const [rejectedPercentage, setRejectedPercentage] = useState(0);
  const [rejectedNumerator, setRejectedNumerator] = useState(0);
  const [rejectedDenominator, setRejectedDenominator] = useState(0);
  const [isLoadingPercentages, setIsLoadingPercentages] = useState(true);

  useEffect(() => {
    let mounted = true;
    setIsLoadingPercentages(true);
    
    Promise.all([
      CommissionContractsStatisticsAPI.getSoldRatio({ startTime: timeRange.start, endTime: timeRange.end }),
      CommissionContractsStatisticsAPI.getReturnedRatio({ startTime: timeRange.start, endTime: timeRange.end }),
      CommissionContractsStatisticsAPI.getRejectedRatio({ startTime: timeRange.start, endTime: timeRange.end })
    ])
      .then(([soldRes, returnedRes, rejectedRes]) => {
        if (!mounted) return;
        setSoldPercentage(soldRes.data?.percentage || 0);
        setSoldNumerator(soldRes.data?.numerator || 0);
        setSoldDenominator(soldRes.data?.denominator || 0);
        setReturnedPercentage(returnedRes.data?.percentage || 0);
        setReturnedNumerator(returnedRes.data?.numerator || 0);
        setReturnedDenominator(returnedRes.data?.denominator || 0);
        setRejectedPercentage(rejectedRes.data?.percentage || 0);
        setRejectedNumerator(rejectedRes.data?.numerator || 0);
        setRejectedDenominator(rejectedRes.data?.denominator || 0);
      })
      .catch(() => {
        if (!mounted) return;
        setSoldPercentage(0);
        setSoldNumerator(0);
        setSoldDenominator(0);
        setReturnedPercentage(0);
        setReturnedNumerator(0);
        setReturnedDenominator(0);
        setRejectedPercentage(0);
        setRejectedNumerator(0);
        setRejectedDenominator(0);
      })
      .finally(() => {
        if (mounted) setIsLoadingPercentages(false);
      });
    
    return () => { mounted = false };
  }, [timeRange.start, timeRange.end]);

  return (
    <PageWrapper>
      <PermissionFlags requiredFlags={['see-employees']}>
        <div className="space-y-6">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-6 w-6" />
              <h1 className="text-3xl font-bold text-foreground">
                {t('stats.commission_contracts.title')}
              </h1>
            </div>
            <InsightsTimeRangeControls timeRange={timeRange} onTimeRangeChange={setTimeRange} />
          </div>

          {/* Total Requests Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <TotalCard
                timeRange={timeRange}
                title={t('stats.commission_contracts.total_requests')}
                icon={<Package className="h-5 w-5" />}
                apiCall={CommissionContractsStatisticsAPI.getTotalRequests}
              />
            </div>
            <div className="lg:col-span-2">
              <ChartCard
                timeRange={timeRange}
                title={t('stats.commission_contracts.total_requests_over_time')}
                apiCall={CommissionContractsStatisticsAPI.getTotalRequestsOverTime}
                dataLabel={t('stats.commission_contracts.requests')}
                backgroundColor="rgba(59, 130, 246, 0.1)"
                borderColor="rgba(59, 130, 246, 0.8)"
              />
            </div>
          </div>

          {/* Total Sales Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <TotalCard
                timeRange={timeRange}
                title={t('stats.commission_contracts.total_sales')}
                icon={<TrendingUp className="h-5 w-5" />}
                apiCall={CommissionContractsStatisticsAPI.getTotalSales}
              />
            </div>
            <div className="lg:col-span-2">
              <ChartCard
                timeRange={timeRange}
                title={t('stats.commission_contracts.total_sales_over_time')}
                apiCall={CommissionContractsStatisticsAPI.getTotalSalesOverTime}
                dataLabel={t('stats.commission_contracts.sales')}
                backgroundColor="rgba(16, 185, 129, 0.1)"
                borderColor="rgba(16, 185, 129, 0.8)"
              />
            </div>
          </div>

          {/* Total In Store Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <TotalCard
                timeRange={timeRange}
                title={t('stats.commission_contracts.total_in_store')}
                icon={<Package className="h-5 w-5" />}
                apiCall={CommissionContractsStatisticsAPI.getTotalInStore}
              />
            </div>
            <div className="lg:col-span-2">
              <ChartCard
                timeRange={timeRange}
                title={t('stats.commission_contracts.total_in_store_over_time')}
                apiCall={CommissionContractsStatisticsAPI.getTotalInStoreOverTime}
                dataLabel={t('stats.commission_contracts.in_store')}
                backgroundColor="rgba(245, 158, 11, 0.1)"
                borderColor="rgba(245, 158, 11, 0.8)"
              />
            </div>
          </div>

          {/* Percentage Statistics Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <PercentageCard
              title={t('stats.commission_contracts.percent_sold_requests')}
              percentage={soldPercentage}
              numerator={soldNumerator}
              denominator={soldDenominator}
              description={t('stats.commission_contracts.requests_status_sold')}
              isLoading={isLoadingPercentages}
            />
            <PercentageCard
              title={t('stats.commission_contracts.percent_returned_requests')}
              percentage={returnedPercentage}
              numerator={returnedNumerator}
              denominator={returnedDenominator}
              description={t('stats.commission_contracts.requests_status_returned')}
              isLoading={isLoadingPercentages}
            />
            <PercentageCard
              title={t('stats.commission_contracts.percent_rejected_requests')}
              percentage={rejectedPercentage}
              numerator={rejectedNumerator}
              denominator={rejectedDenominator}
              description={t('stats.commission_contracts.requests_status_rejected')}
              isLoading={isLoadingPercentages}
            />
          </div>
        </div>
      </PermissionFlags>
    </PageWrapper>
  );
}

export default CommissionContractsStatisticsPage;

